import React from 'react';
import { FieldError } from '../ErrorDisplay';
import { TabProps } from './types';
import { getPriorityClasses, getPriorityIndicator, isVisibleAtDepth } from './helpers';

const Tab0BasicInfo: React.FC<TabProps> = ({
  formData,
  updateFormData,
  updateNestedField,
  showValidation,
  validationErrors,
  depth,
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Handicap{getPriorityIndicator('handicap')}
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="-5"
            max="40"
            value={formData.handicap || ''}
            onChange={(e) => updateFormData({ handicap: e.target.value ? parseFloat(e.target.value) : undefined })}
            className={getPriorityClasses('handicap', !formData.handicap)}
          />
          {showValidation && (
            <FieldError errors={validationErrors} field="Handicap" />
          )}
        </div>
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Age{getPriorityIndicator('age')}
          </label>
          <input
            type="number"
            inputMode="numeric"
            min="10"
            max="100"
            value={formData.age || ''}
            onChange={(e) => updateFormData({ age: e.target.value ? parseFloat(e.target.value) : undefined })}
            className={getPriorityClasses('age', !formData.age)}
          />
          {showValidation && (
            <FieldError errors={validationErrors} field="Age" />
          )}
        </div>
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Height (inches){getPriorityIndicator('height_inches')}
          </label>
          <input
            type="number"
            inputMode="numeric"
            min="48"
            max="84"
            value={formData.height_inches || ''}
            onChange={(e) => updateFormData({ height_inches: e.target.value ? parseFloat(e.target.value) : undefined })}
            className={getPriorityClasses('height_inches', !formData.height_inches)}
          />
        </div>
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Years Playing Golf{getPriorityIndicator('years_playing')}
          </label>
          <input
            type="number"
            min="0"
            max="80"
            value={formData.years_playing || ''}
            onChange={(e) => updateFormData({ years_playing: e.target.value ? parseFloat(e.target.value) : undefined })}
            className={getPriorityClasses('years_playing', !formData.years_playing)}
          />
        </div>
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Rounds Per Year{getPriorityIndicator('rounds_per_year')}
          </label>
          <input
            type="number"
            min="0"
            max="200"
            value={formData.rounds_per_year || ''}
            onChange={(e) => updateFormData({ rounds_per_year: e.target.value ? parseFloat(e.target.value) : undefined })}
            className={getPriorityClasses('rounds_per_year', !formData.rounds_per_year)}
          />
        </div>
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Dominant Hand{getPriorityIndicator('dominant_hand')}
          </label>
          <select
            value={formData.dominant_hand || ''}
            onChange={(e) => updateFormData({ dominant_hand: e.target.value || undefined })}
            className={getPriorityClasses('dominant_hand', !formData.dominant_hand)}
          >
            <option value="">Select</option>
            <option value="Right">Right</option>
            <option value="Left">Left</option>
          </select>
        </div>
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Gender
          </label>
          <select
            value={formData.gender || ''}
            onChange={(e) => updateFormData({ gender: e.target.value || undefined })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>
      </div>

      {/* F3: folded in from the former Physical Profile tab — no scorer needs its own tab for 5 fields */}
      {(depth === 'everything' || depth === 'plus') && (
        <div className="pt-2">
          <h4 className="font-medium text-gray-900 mb-3">Physical Profile</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Wrist to Floor (inches for club length){getPriorityIndicator('physical_profile.wrist_to_floor')}
              </label>
              <input
                type="number"
                min="25"
                max="40"
                step="0.5"
                value={formData.physical_profile?.wrist_to_floor || ''}
                onChange={(e) => updateNestedField('physical_profile', {
                  wrist_to_floor: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className={getPriorityClasses('physical_profile.wrist_to_floor', !formData.physical_profile?.wrist_to_floor)}
              />
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Flexibility Rating (1-10, 10 = most flexible){getPriorityIndicator('physical_profile.flexibility_rating')}
              </label>
              <select
                value={formData.physical_profile?.flexibility_rating || ''}
                onChange={(e) => updateNestedField('physical_profile', {
                  flexibility_rating: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className={getPriorityClasses('physical_profile.flexibility_rating', !formData.physical_profile?.flexibility_rating)}
              >
                <option value="">Select</option>
                <option value="1">1 - Very Limited</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5 - Average</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10 - Very Flexible</option>
              </select>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Core Strength (1-10, 10 = strongest){getPriorityIndicator('physical_profile.core_strength')}
              </label>
              <select
                value={formData.physical_profile?.core_strength || ''}
                onChange={(e) => updateNestedField('physical_profile', {
                  core_strength: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className={getPriorityClasses('physical_profile.core_strength', !formData.physical_profile?.core_strength)}
              >
                <option value="">Select</option>
                <option value="1">1 - Very Weak</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5 - Average</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10 - Very Strong</option>
              </select>
            </div>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Weekly Workouts{getPriorityIndicator('physical_profile.weekly_workouts')}
              </label>
              <select
                value={formData.physical_profile?.weekly_workouts || ''}
                onChange={(e) => updateNestedField('physical_profile', {
                  weekly_workouts: e.target.value || undefined
                })}
                className={getPriorityClasses('physical_profile.weekly_workouts', !formData.physical_profile?.weekly_workouts)}
              >
                <option value="">Select</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2-4">2-4</option>
                <option value="4+">4+</option>
              </select>
            </div>
            {isVisibleAtDepth('physical_profile.posture_type', depth) && (
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Posture Type{getPriorityIndicator('physical_profile.posture_type')}
                </label>
                <select
                  value={formData.physical_profile?.posture_type || ''}
                  onChange={(e) => updateNestedField('physical_profile', {
                    posture_type: e.target.value || undefined
                  })}
                  className={getPriorityClasses('physical_profile.posture_type', !formData.physical_profile?.posture_type)}
                >
                  <option value="">Select</option>
                  <option value="upright">Upright</option>
                  <option value="athletic">Athletic</option>
                  <option value="hunched">Hunched</option>
                </select>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tab0BasicInfo;
